import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Select, FormControl, InputLabel,
  Alert, Tooltip, Skeleton, Checkbox, FormGroup, FormControlLabel,
  Collapse, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth } from '../../auth/AuthContext';
import ApiErrorAlert from '../../components/ApiErrorAlert';
import {
  listWebhooks, createWebhook, updateWebhook, deleteWebhook,
  getWebhookDeliveries, retryWebhookDelivery,
} from '../../api/developerApi';

// Currently-available webhook event types. The API's event catalog is the
// canonical source; keep this list in sync when new event types are added so
// the subscription checkboxes stay complete.
const ALL_EVENTS = ['document.indexed', 'document.failed', 'record.indexed', 'record.failed'];

// ---------------------------------------------------------------------------
// Delivery status chip
// ---------------------------------------------------------------------------

const DELIVERY_STATUS_STYLES = {
  DELIVERED: { bgcolor: '#dcfce7', color: '#166534' },
  PENDING:   { bgcolor: '#fef9c3', color: '#854d0e' },
  FAILED:    { bgcolor: '#fee2e2', color: '#991b1b' },
};

function DeliveryStatusChip({ status }) {
  const s = DELIVERY_STATUS_STYLES[status] || { bgcolor: '#f4f4f5', color: '#52525b' };
  return <Chip label={status} size="small" sx={{ ...s, fontWeight: 600, fontSize: 10 }} />;
}

// ---------------------------------------------------------------------------
// Delivery history panel (expandable per webhook)
// ---------------------------------------------------------------------------

function DeliveryPanel({ webhookId, tenantId, open }) {
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery(
    ['deliveries', webhookId],
    () => getWebhookDeliveries(webhookId, { tenantId, limit: 20 }),
    { enabled: open && !!webhookId, staleTime: 30_000 }
  );

  const retryMutation = useMutation(
    (deliveryId) => retryWebhookDelivery(webhookId, deliveryId),
    { onSuccess: () => qc.invalidateQueries(['deliveries', webhookId]) }
  );

  if (!open) return null;

  return (
    <Box sx={{ bgcolor: 'grey.50', px: 3, py: 2, borderTop: '1px solid #E4E4E7' }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Recent Deliveries
      </Typography>

      {isLoading && <CircularProgress size={20} />}
      {error && <Typography variant="caption" color="error">Failed to load deliveries</Typography>}
      <ApiErrorAlert error={retryMutation.error} onClose={() => retryMutation.reset()} sx={{ mb: 1.5 }} />

      {!isLoading && !error && (!data || data.length === 0) && (
        <Typography variant="caption" color="text.secondary">No deliveries yet.</Typography>
      )}

      {!isLoading && data && data.length > 0 && (
        <Table size="small" sx={{ bgcolor: 'white', borderRadius: 1, overflow: 'hidden' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: 11, width: 160 }}>Time</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 11, width: 140 }}>Event</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 11, width: 100 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 11, width: 80 }}>Attempts</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 11 }}>Next retry</TableCell>
              <TableCell sx={{ width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((d) => (
              <TableRow key={d.id} hover>
                <TableCell sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary' }}>
                  {d.createdAt ? new Date(d.createdAt).toLocaleString() : '—'}
                </TableCell>
                <TableCell sx={{ fontSize: 11 }}>
                  <Chip label={d.eventType} size="small" sx={{ fontSize: 10 }} />
                </TableCell>
                <TableCell><DeliveryStatusChip status={d.status} /></TableCell>
                <TableCell sx={{ fontSize: 11, textAlign: 'center' }}>{d.attempts ?? 0}</TableCell>
                <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                  {d.nextRetryAt
                    ? new Date(d.nextRetryAt).toLocaleString()
                    : d.status === 'FAILED' ? 'No more retries' : '—'}
                </TableCell>
                <TableCell align="right">
                  {d.status === 'FAILED' && (
                    <Tooltip title="Retry delivery">
                      <IconButton size="small" color="primary"
                        aria-label="Retry delivery"
                        disabled={retryMutation.isLoading}
                        onClick={() => retryMutation.mutate(d.id)}>
                        <ReplayIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

function WebhookForm({ register, control, formId, onSubmit }) {
  return (
    <Box component="form" id={formId} onSubmit={onSubmit} noValidate
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <TextField label="Endpoint URL" size="small" fullWidth placeholder="https://yourapp.com/webhooks/vectros"
        {...register('url', { required: true })} />
      <Typography variant="caption" fontWeight={600} color="text.secondary">Events to subscribe</Typography>
      <Controller name="events" control={control} render={({ field }) => (
        <FormGroup>
          {ALL_EVENTS.map((e) => (
            <FormControlLabel key={e} label={<Typography variant="body2">{e}</Typography>}
              control={
                <Checkbox size="small" checked={field.value?.includes(e)}
                  onChange={(ev) => {
                    const next = ev.target.checked
                      ? [...(field.value || []), e]
                      : (field.value || []).filter((x) => x !== e);
                    field.onChange(next);
                  }} />
              } />
          ))}
        </FormGroup>
      )} />
      <Typography variant="caption" color="text.secondary">
        These are the event types currently available to subscribe to. We add new
        types over time — check the API reference for the latest catalog.
      </Typography>
    </Box>
  );
}

export default function WebhooksPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [env, setEnv] = useState('live');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [newSecret, setNewSecret] = useState(null);
  const [expandedWebhook, setExpandedWebhook] = useState(null);

  const tenantId = env === 'live' ? user?.liveTenantId : user?.testTenantId;

  const { data: webhooks, isLoading } = useQuery(
    ['webhooks', tenantId], () => listWebhooks(tenantId), { enabled: !!tenantId }
  );

  // Separate form instances — prevents state bleed between create and edit dialogs
  const createForm = useForm({ defaultValues: { url: '', events: [] } });
  const editForm = useForm({ defaultValues: { url: '', events: [] } });

  const createMutation = useMutation(createWebhook, {
    onSuccess: (data) => {
      // Optional-chain a malformed/empty body — `data.secret` would otherwise
      // throw inside onSuccess (sibling of #98).
      if (data?.secret) setNewSecret(data.secret);
      qc.invalidateQueries(['webhooks', tenantId]);
      createForm.reset();
    },
  });

  const updateMutation = useMutation(({ id, body }) => updateWebhook(id, body), {
    onSuccess: () => { setEditTarget(null); qc.invalidateQueries(['webhooks', tenantId]); },
  });

  const deleteMutation = useMutation(deleteWebhook, {
    onSuccess: () => qc.invalidateQueries(['webhooks', tenantId]),
  });

  const reenableMutation = useMutation((id) => updateWebhook(id, { status: 'ACTIVE' }), {
    onSuccess: () => qc.invalidateQueries(['webhooks', tenantId]),
  });

  const onSubmitCreate = (values) => createMutation.mutate({ ...values, tenantId });

  const onSubmitEdit = (values) => updateMutation.mutate({ id: editTarget.id, body: values });

  const handleCloseCreate = () => {
    setCreateOpen(false);
    setNewSecret(null);
    createForm.reset();
    createMutation.reset(); // clear any prior error so it doesn't flash on reopen
  };

  const handleOpenEdit = (w) => {
    editForm.reset({ url: w.url, events: w.events });
    updateMutation.reset(); // fresh dialog: drop any stale error
    setEditTarget(w);
  };

  const handleCloseEdit = () => {
    setEditTarget(null);
    updateMutation.reset();
  };

  const disabledCount = (webhooks || []).filter((w) => w.status === 'DISABLED').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Webhooks</Typography>
          <Typography variant="body2" color="text.secondary">Receive real-time event notifications</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Environment</InputLabel>
            <Select value={env} label="Environment" onChange={(e) => setEnv(e.target.value)}>
              <MenuItem value="live">Live</MenuItem>
              <MenuItem value="test">Test</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
            Add webhook
          </Button>
        </Box>
      </Box>

      {disabledCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {disabledCount} webhook{disabledCount > 1 ? 's are' : ' is'} disabled due to consecutive delivery failures.
          Re-enable below after fixing your endpoint.
        </Alert>
      )}

      <ApiErrorAlert
        error={deleteMutation.error || reenableMutation.error}
        onClose={() => { deleteMutation.reset(); reenableMutation.reset(); }}
        sx={{ mb: 2 }}
      />

      <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>URL</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Events</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 2 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 4 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}
                </TableRow>
              ))
              : (webhooks || []).map((w) => (
                <React.Fragment key={w.id}>
                <TableRow hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setExpandedWebhook(e => e === w.id ? null : w.id)}>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {w.url}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {(w.events || []).map((e) => (
                      <Chip key={e} label={e} size="small" sx={{ mr: 0.5, mb: 0.5, fontSize: 10 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip label={w.status} size="small"
                      sx={{
                        bgcolor: w.status === 'ACTIVE' ? '#dcfce7' : '#fee2e2',
                        color: w.status === 'ACTIVE' ? '#166534' : '#991b1b',
                        fontWeight: 600, fontSize: 11,
                      }} />
                    {w.disabledReason && (
                      <Typography variant="caption" display="block" color="error.main" sx={{ fontSize: 10 }}>
                        {w.disabledReason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()}>
                    <Tooltip title={expandedWebhook === w.id ? 'Hide deliveries' : 'Show deliveries'}>
                      <IconButton size="small"
                        aria-label={expandedWebhook === w.id ? 'Hide deliveries' : 'Show deliveries'}
                        onClick={() => setExpandedWebhook(e => e === w.id ? null : w.id)}>
                        {expandedWebhook === w.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    {w.status === 'DISABLED' && (
                      <Tooltip title="Re-enable">
                        <IconButton size="small" color="primary" aria-label="Re-enable webhook" onClick={() => reenableMutation.mutate(w.id)}>
                          <ReplayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton size="small" aria-label="Edit webhook" onClick={() => handleOpenEdit(w)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" aria-label="Delete webhook" onClick={() => deleteMutation.mutate(w.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expandedWebhook === w.id} unmountOnExit>
                      <DeliveryPanel
                        webhookId={w.id}
                        tenantId={tenantId}
                        open={expandedWebhook === w.id}
                      />
                    </Collapse>
                  </TableCell>
                </TableRow>
                </React.Fragment>
              ))}
            {!isLoading && (webhooks || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.primary">No webhooks yet.</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Add a webhook to receive real-time event notifications.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add webhook</DialogTitle>
        <DialogContent>
          {newSecret ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Copy your webhook secret now — it won't be shown again.
              </Alert>
              <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'background.default', border: '1px solid #E4E4E7', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flexGrow: 1, fontSize: 12 }}>
                  {newSecret}
                </Typography>
                <IconButton size="small" aria-label="Copy webhook secret" onClick={() => navigator.clipboard.writeText(newSecret)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Paper>
            </Box>
          ) : (
            <>
              <ApiErrorAlert error={createMutation.error} sx={{ mb: 2 }} />
              <WebhookForm formId="webhook-create-form" onSubmit={createForm.handleSubmit(onSubmitCreate)}
                register={createForm.register} control={createForm.control} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {newSecret
            ? <Button onClick={handleCloseCreate} variant="contained"
                sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none' }}>Done</Button>
            : <>
                <Button onClick={handleCloseCreate} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button type="submit" form="webhook-create-form" variant="contained"
                  disabled={createMutation.isLoading}
                  sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
                  {createMutation.isLoading ? 'Adding…' : 'Add'}
                </Button>
              </>
          }
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editTarget} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit webhook</DialogTitle>
        <DialogContent>
          <ApiErrorAlert error={updateMutation.error} sx={{ mb: 2 }} />
          <WebhookForm formId="webhook-edit-form" onSubmit={editForm.handleSubmit(onSubmitEdit)}
            register={editForm.register} control={editForm.control} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEdit} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button type="submit" form="webhook-edit-form" variant="contained"
            disabled={updateMutation.isLoading}
            sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
            {updateMutation.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
